// Libraries
import React from 'react'
import { ModuleNames } from '../../../../../constants/componentNames.constants'
import Layout from '../../../../layouts/layout'

// Webinar Recording Page Modules
import Button from '../../../../global/button'
import ContentBlock from '../../../../global/contentBlock'
import MainCTA from '../../../../global/mainCta'
import ResourceRow from '../../../../global/resourceRow'
import SEOTags from '../../../../global/seoTags'
import VideoSpotlight from '../../../../global/videoSpotlight'

// Helpers
import ImgUtil from '../../../../../utils/img.utils'
import { renderRichText } from '../../../../../utils/richTextHandler.utils'
import formatInlineCode from '../../../../../utils/shortTextFormatting'

// Constants
import { LocaleCodes } from '../../../../../constants/localization.constants'

// helpers
import { findTarget } from '../../../../../utils/microcopy.utils'
import GlobalContainer from '../../../../global/globalContainer'
import Typography from '../../../../global/typography'

import Hero from '../../../../global/hero'
import DynamicRow from '../../../../global/relatedResources'
import WrapperImage from '../../../../global/wrapperImage'
import {
  WebinarRecordingBodyContainer,
  WebinarRecordingHeroContainer,
  WebinarRecordingInlineAuthor,
  WebinarRecordingSpeakersContainer,
} from './style'

const WebinarRecordingPage = ({
  path,
  pageContext,
  location,
  headerData,
  footerData,
  announcementData,
  mainNavBannerData,
  browserLanguageData,
}: any) => {
  if (pageContext && pageContext.webinarRecordingData) {
    let dynamicPageContent: any[],
      pageType = 'webinarRecording',
      pageData = pageContext?.webinarRecordingData || null,
      microcopy: { key: string; value: string }[] = pageData?.microcopy?.values || [],
      flexSection = pageData.flexSection || [],
      rtfBody = pageData.body || null,
      referrer = pageData.referrer || null,
      speakers: {}[] = [],
      faqText = pageData.faqText || '',
      faqCta: { externalUrl: string; text: string } | undefined = pageData.faqCta || undefined,
      finePrint = pageData.finePrint || null,
      displayDynamicRow: boolean = true

    // populate body section with referrer entry
    if (referrer) {
      if (referrer.body) {
        rtfBody = referrer.body
      }
      if (referrer.bioRTF?.references && referrer.bioRTF?.references?.length > 0) {
        speakers = referrer.bioRTF.references
      }
    }

    let defaultLocale: string
    if (pageContext && pageContext.pageLocale) {
      defaultLocale = pageContext.pageLocale
    } else {
      defaultLocale = LocaleCodes.ENGLISH.code
    }

    const countryCode = defaultLocale.slice(3)
    const number = pageData?.resourceCategory?.number
    const englishRecordingRow = `resource.${number}.RecordingROW.text`
    const globalRecordingRow = `resource.${countryCode}.${number}.RecordingROW.text`

    const dynamicRow = (
      <DynamicRow
        resourseRowIntro={findTarget(
          defaultLocale === LocaleCodes.ENGLISH.code ? englishRecordingRow : globalRecordingRow,
          microcopy
        )}
        relatedResources={pageContext.relatedResources || []}
        location={location}
        locale={defaultLocale}
      />
    )

    if (flexSection.length > 0) {
      dynamicPageContent = flexSection.map((module: any, i: Number) => {
        switch (module.__typename) {
          case ModuleNames.GLOBAL_CONTENT_BLOCK:
            return <ContentBlock data={module} key={`page-component--${i}`}></ContentBlock>

          case ModuleNames.RESOURCE_ROW:
            // disable the dynamic row if the module is present
            displayDynamicRow = false
            return (
              <ResourceRow
                data={module}
                key={`page-component--${i}`}
                loacle={defaultLocale}></ResourceRow>
            )
          case ModuleNames.GLOBAL_MAIN_CTA:
            // disable the dynamic row if the module is present
            return <MainCTA data={module} key={`page-component--${i}`}></MainCTA>
        }
      })
    }

    const englishSpeakerText = `resource.${number}.speakers.text`
    const globalSpeakerText = `resource.${countryCode}.${number}.speakers.text`
    const speakerText = findTarget(
      defaultLocale === LocaleCodes.ENGLISH.code ? englishSpeakerText : globalSpeakerText,
      microcopy
    )

    const renderSpeakers = () => (
      <WebinarRecordingSpeakersContainer>
        <Typography className="WebinarRecordingSpeakers__title" type="labelAlt">
          {speakerText}
        </Typography>
        {speakers.map((speaker: any, i: Number) => {
          if (speaker.__typename === ModuleNames.BIO) {
            const data = speaker
            const imageSrc =
              data.headshot &&
              data.headshot.image &&
              data.headshot.image.file &&
              data.headshot.image.file.url
                ? ImgUtil.getResizedImageUrl(data.headshot.image.file.url, `width=250`)
                : '/'

            const imageAlt =
              data.headshot && data.headshot.altText ? data.headshot.altText : 'Headshot image'

            const authorFirstName = data.firstName ? data.firstName : ''
            const authorLastName = data.lastName ? data.lastName : ''
            const authorName = authorFirstName + ' ' + authorLastName
            const authorTitle = data.role ? data.role : ''
            const authorCompany = data.company ? data.company : ''

            let byline
            if (authorTitle && authorCompany) {
              byline = `${authorTitle} at ${authorCompany}`
            } else if (authorTitle) {
              byline = authorTitle
            } else if (authorCompany) {
              byline = authorCompany
            }
            return (
              <WebinarRecordingInlineAuthor>
                <div className="InlineAuthor__Headshot-Img">
                  <WrapperImage
                    className="InlineAuthor__Headshot-Img-File"
                    alt={imageAlt}
                    src={imageSrc}
                    width={80}
                    height={80}
                    threshold={650}
                  />
                </div>
                <div className="InlineAuthor__Information">
                  <Typography className="WebinarRecording__InlineAuthor__Name" type="label">
                    {authorName}
                  </Typography>
                  <Typography className="WebinarRecording__InlineAuthor__Byline" type="caption">
                    {byline}
                  </Typography>
                </div>
              </WebinarRecordingInlineAuthor>
            )
          }
        })}
      </WebinarRecordingSpeakersContainer>
    )

    const renderHeroSpotlight = () => (
      <>
        <WebinarRecordingHeroContainer>
          <GlobalContainer>
            <Hero
              data={{
                headline: pageData?.h1Title || '',
              }}
              location={location}
              pageTitle={pageTitle}
              showBreadcrumbs={true}
              pageType={pageType}
              locale={defaultLocale}
              breadcrumb={pageData.breadcrumb}
            />
            <div className="WebinarRecording__clock_date_Container">
              <WrapperImage
                src="https://images.ctfassets.net/5965pury2lcm/3cKpn0HNi5gLFFlLsECOOz/c41fb5a4847f301edd1b64837b4d09c2/Clock2clockWhite.png"
                height={22}
                width={22}
                threshold={650}
              />
              <Typography className="WebinarRecording__date" type="label">
                {pageData.webinarDate}
              </Typography>
            </div>
          </GlobalContainer>
        </WebinarRecordingHeroContainer>
        {pageData.spotlight && (
          <VideoSpotlight data={pageData.spotlight} useCase={pageData.spotlight?.useCase || ''} />
        )}
      </>
    )

    const pageBody = (
      <GlobalContainer className="WebinarRecording__Outer-Container">
        <WebinarRecordingBodyContainer>
          <div className="WebinarRecording__Body__left-container">
            {rtfBody && <div className="WebinarRecording__Body">{renderRichText(rtfBody)}</div>}
            {finePrint && (
              <div className="WebinarRecording__fineprint">{renderRichText(finePrint)}</div>
            )}
          </div>
          <div className="WebinarRecording__Body__right-container">
            {speakers && speakers.length > 0 && (
              <div className="WebinarRecording__Speakers-container">{renderSpeakers()}</div>
            )}
            {faqText && (
              <>
                <Typography className="WebinarRecording__FAQ-text" type="body">
                  {formatInlineCode(pageData.faqText)}
                </Typography>
                {faqCta && faqCta.text && faqCta.externalUrl && (
                  <Button
                    type="text-link"
                    theme="light"
                    text={faqCta.text}
                    icon={false}
                    href={faqCta.externalUrl}
                  />
                )}
              </>
            )}
          </div>
        </WebinarRecordingBodyContainer>
      </GlobalContainer>
    )

    const defaultSlug = pageData && pageData.slug ? pageData.slug : '/'
    const seoData = pageData && pageData.seoMetadata ? pageData.seoMetadata : null

    const siteSection = 'Resource'
    const pageTitle = pageData.pageName ? pageData.pageName : ''
    const pageInner = (
      <div data-cy={`WebinarRecording`}>
        <SEOTags
          seoMetadata={seoData}
          slug={defaultSlug}
          path={path}
          locale={defaultLocale}
          siteSection={siteSection}
          availableLocales={pageContext.availableLocales}
        />
        {renderHeroSpotlight()}
        {pageBody}
        {displayDynamicRow ? dynamicRow : ''}
        {dynamicPageContent}
      </div>
    )

    return (
      <Layout.Template
        lang={defaultLocale}
        headerData={headerData}
        footerData={footerData}
        announcementData={announcementData}
        mainNavBannerData={mainNavBannerData}
        browserLanguageData={browserLanguageData}
        minimalFooter={true}
        slug={defaultSlug}
        availableLocales={pageContext.availableLocales}>
        {pageInner}
      </Layout.Template>
    )
  }
  return null
}

export default WebinarRecordingPage
